import styled from '@emotion/styled';
import {
	Checkbox,
	CreditCardIcon,
	TextInput,
	useBrandDetails,
} from 'dw-au-react-components';
import React, { useEffect, useState } from 'react';
import { EditIcon } from '../../svg';
import PayPalLogo from '../../svg/PayPalLogo';
import CartHeader from '../ShoppingCart/CartHeader';
import MemberAddressLayout from './MemberAddressLayout';
import useForm from 'react-hook-form';
import { VoucherCodeForm } from '../Forms/VoucherCodeForm';
import ReCAPTCHA from 'react-google-recaptcha';

const StyledMemberCheckout = styled.form`
	.cvv-input {
		margin-left: auto;
		max-width: 100px;
	}

	#receiveEmail {
		width: 5rem;
	}
`;

function MemberCheckout({
	userData,
	handleMemberShipping,
	handleMemberBilling,
	setStep,
	showNewCard,
	paymentMethod,
	handleMemberCheckout,
	message = '',
	setCVV,
	setMessage,
	showVouchers,
	logoutUser,
	setReceiveEmail,
	receiveEmail,
}) {
	let paymentWallet = userData?.trustedDetails?.paymentWallet;
	let billingAddress = userData?.trustedDetails?.billingAddress;
	let shippingAddress = userData?.trustedDetails?.shippingAddress;

	const { register, errors, handleSubmit } = useForm({
		mode: 'onChange',
	});
	const isHideCaptcha =
		window.location.hostname.includes('uat.') ||
		window.location.hostname.includes('st.') ||
		window.location.hostname.includes('sun-webdev') ||
		window.location.hostname.includes('rebrand.');
	const { name, googleReCaptchaSiteKey } = useBrandDetails();
	const [defaultShippingAddress, setDefaultShippingAddress] = useState(null);
	const [defaultPayment, setDefaultPayment] = useState(null);
	const [captchaVerified, setCaptchaVerified] = useState(
		isHideCaptcha ? true : false
	);
	const [isCaptchaLoaded, setIsCaptchaLoaded] = useState(
		isHideCaptcha ? true : false
	);
	const [captchaError, setCaptchaError] = useState(false);

	const getDefaultShippingAddress = () => {
		let filteredAddress = shippingAddress?.filter((address, index) => {
			return address.defaultAddress === true;
		});

		if (!filteredAddress?.length > 0) {
			filteredAddress = shippingAddress?.filter((address, index) => {
				return index === 0;
			});
		}
		setDefaultShippingAddress(filteredAddress);
	};

	const getDefaultPayment = () => {
		let filteredPayment = paymentWallet?.filter((payment, index) => {
			return payment.defaultPayment === true;
		});

		if (!filteredPayment?.length > 0) {
			filteredPayment = paymentWallet?.filter((payment, index) => {
				return index === 0;
			});
		}
		setDefaultPayment(filteredPayment);
	};

	useEffect(() => {
		getDefaultShippingAddress();
		getDefaultPayment();
		if (showNewCard && paymentMethod !== 'paypal') {
			setDefaultPayment([showNewCard]);
		} else if (paymentMethod === 'paypal') {
			setDefaultPayment(null);
		}
	}, []);

	let cvvLength =
		defaultPayment && defaultPayment[0]?.cardType === 'AmericanExpress' ? 4 : 3;

	let error = false;
	let helperText = 'Required';
	if (errors.cvv) {
		error = true;
		helperText = errors.cvv.message;
	}

	const handleCvvInput = () => {};

	const checkCvvInput = (cvvInput) => {
		let inputLength = cvvInput.length;

		if (!isNaN(cvvInput) && inputLength === cvvLength) {
			return true;
		} else {
			return `Please make sure that your CVV number is ${cvvLength} digits`;
		}
	};

	const onSubmit = (data) => {
		setCaptchaError(false);
		if (
			defaultShippingAddress?.length > 0 &&
			(defaultPayment?.length > 0 || paymentMethod === 'paypal')
		) {
			if (isCaptchaLoaded && captchaVerified) {
				handleMemberCheckout();
				if (defaultPayment?.length > 0) {
					setCVV(data.cvv);
				}
			} else {
				setCaptchaError(true);
			}
		} else {
			setMessage({
				class: 'alert-danger',
				message: 'Please fill all the fields!',
			});
			setTimeout(() => {
				setMessage(null);
			}, 2000);
		}
	};

	return (
		<StyledMemberCheckout onSubmit={handleSubmit(onSubmit)}>
			<CartHeader title='Checkout' />
			{message && (
				<div className={`mb-20 alert ${message?.class}`}>{message.message}</div>
			)}

			<div className='subtitle mb-16 lower roboto'>Ship to</div>
			<div className='border-bottom-light-medium mb-40'>
				{defaultShippingAddress?.length > 0 ? (
					<>
						{defaultShippingAddress?.map((filteredAddress, index) => {
							return (
								<div
									className='d-flex justify-content-between align-items-center mb-20'
									key={index}
								>
									<MemberAddressLayout filteredAddress={filteredAddress} />
									<div
										className='cursor-pointer'
										onClick={() => handleMemberShipping(filteredAddress, 5)}
									>
										<EditIcon />
									</div>
								</div>
							);
						})}
					</>
				) : (
					<div className='dmlp__form__input__error mb-25'>
						No Shipping Address found.
					</div>
				)}
				<div
					className='text-red mb-40 cursor-pointer d-inline-block'
					onClick={() => setStep(6)}
				>
					{defaultShippingAddress?.length > 0 && 'Change or'} Add Address
				</div>
			</div>

			<div className='subtitle mb-16 lower roboto'>Bill to</div>
			<div className='border-bottom-light-medium mb-40'>
				{billingAddress ? (
					<div className='d-flex justify-content-between align-items-center mb-40'>
						<MemberAddressLayout filteredAddress={billingAddress} />
						<div
							className='cursor-pointer'
							onClick={() => handleMemberBilling(billingAddress, 5)}
						>
							<EditIcon />
						</div>
					</div>
				) : (
					<div className='dmlp__form__input__error mb-25'>
						No Billing Address found.
					</div>
				)}
			</div>

			<div className='subtitle mb-16 lower roboto'>Pay with</div>
			<div className='border-bottom-light-medium mb-40'>
				{defaultPayment?.length > 0 ? (
					<>
						{defaultPayment?.map((filteredPayment, index) => {
							return (
								<div key={index} className='d-flex mb-20'>
									<CreditCardIcon
										className='mr-25'
										cardType={filteredPayment?.cardType}
									/>

									<div className='text-dark-medium'>
										<div className='mb-10 text-dark-medium'>
											{filteredPayment?.cardNumber?.replace(
												/.{4}(?=.{4})/g,
												'xxxx '
											)}

											{filteredPayment?.creditCardNumber?.replace(
												/.{4}(?=.{4})/g,
												'xxxx '
											)}
										</div>
										Expiry: {filteredPayment?.expirationMonth}/
										{filteredPayment?.expirationYear}
									</div>

									{!showNewCard && (
										<div className='cvv-input'>
											<TextInput
												autoComplete='off'
												cypressClass='cvv'
												onChange={handleCvvInput}
												maxLength={cvvLength}
												error={error}
												type='text'
												label='CVV'
												name='cvv'
												helperText={helperText}
												register={register({
													required: 'Missing CVV',
													validate: {
														validateCvv: (cvv) => checkCvvInput(cvv),
													},
												})}
											/>
										</div>
									)}
								</div>
							);
						})}
					</>
				) : !defaultPayment && paymentMethod === 'paypal' ? (
					<div className='mb-25'>
						<div className='paypal-method mb-10'>
							<PayPalLogo />
						</div>
						<div className='text-dark-medium'>
							You will be redirected to PayPal to complete purchase securely
						</div>
					</div>
				) : (
					<div className='dmlp__form__input__error mb-25'>No Cards found.</div>
				)}
				<div
					className='text-red mb-40 cursor-pointer d-inline-block'
					onClick={() => setStep(9)}
				>
					{paymentWallet?.length > 0 && 'Change or'} Choose Payment Method
				</div>
			</div>

			{showVouchers && (
				<div className='border-bottom-light-medium mb-40'>
					<div className='subtitle mb-16 lower roboto'>Add Voucher Code</div>
					<VoucherCodeForm />
				</div>
			)}

			<div className='mb-35'>
				{/* <Checkbox
					label={`By continuing, you confirm you are over 18 years of age and agree to the <a href="/terms" target="_blank">Terms and Conditions</a> and have read the
					<a href="/privacy" target="_blank">Privacy Policy</a>. You can manage your marketing preferences via your ${name} account.`}
					name='recieveEmail'
					// register={register}
					// checked={receiveEmail}
					register={register({ required: 'This field is required.' })}
					onChange={() => setReceiveEmail(!receiveEmail)}
				/> */}
				<div className='d-flex justify-content-start align-items-start'>
					<input
						type='checkbox'
						id='receiveEmail'
						name='recieveEmail'
						ref={register({
							required:
								'Please tick the above box to proceed with your order today.',
						})}
						onChange={() => setReceiveEmail(!receiveEmail)}
					/>
					<label htmlFor='receiveEmail' className='pl-10'>
						By continuing, you confirm you are over 18 years of age and agree to
						the{' '}
						<a href='/terms' target='_blank'>
							Terms and Conditions
						</a>{' '}
						and have read the{' '}
						<a href='/privacy' target='_blank'>
							Privacy Policy
						</a>
						. You can manage your marketing preferences via your {name} account.
					</label>
				</div>
				{errors.recieveEmail ? (
					<div className='text-red font-weight-light'>
						{errors?.recieveEmail?.message}
					</div>
				) : null}
			</div>

			<div className='checkout-verification form-row mb-20'>
				<ReCAPTCHA
					sitekey={googleReCaptchaSiteKey}
					onChange={(value) => {
						setCaptchaVerified(value ? true : false);
					}}
					asyncScriptOnLoad={() => {
						setIsCaptchaLoaded(true);
					}}
				/>
			</div>

			{captchaError && !captchaVerified && isCaptchaLoaded ? (
				<div className='text-red font-weight-light mb-20'>
					Please confirm you are not a robot by clicking the above checkbox.
				</div>
			) : null}

			<div className='form-row'>
				<button
					type='submit'
					className='btn btn-primary font-weight-bold dmlp__btn w-100 mb-20 dmlp_member_place_order'
				>
					Confirm & Pay
				</button>
				<div
					className='btn btn-outline-secondary btn-block dmlp_member_back_to_cart'
					onClick={() => {
						setStep(0);
					}}
				>
					Go back to your Cart
				</div>
			</div>

			<div className='mt-40 text-center'>
				<span
					className='text text_big d-inline-block cursor-pointer'
					onClick={() => logoutUser()}
				>
					<u>Sign out of your account.</u>
				</span>
			</div>
		</StyledMemberCheckout>
	);
}

export default MemberCheckout;
